import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {EnvService} from '../services/env.service';
import {AlertService} from '../services/alert.service';
import {DialogService} from '../services/dialog.service';

interface DatabaseSqlConnection {
  id: string;
  name: string;
  sqlServerTypeId: string;
  connectionType: string;
  host: string;
}

@Component({
  selector: 'micro-cmdb-database-sql-servers',
  templateUrl: './cmdb-database-sql-servers.component.html'
})
export class CmdbDatabaseSqlServersComponent implements OnChanges, OnDestroy {
  @Input() databaseId: string;
  @Input() sqlServerTypeId: string;
  connections: DatabaseSqlConnection[] = [];
  selectedConnectionId: string;
  selectedConnection: DatabaseSqlConnection;
  loading = false;
  saving = false;
  loadFailed = false;
  private subscriptions = new Subscription();
  private listSubscription?: Subscription;

  constructor(private http: HttpClient, private env: EnvService,
              private alerts: AlertService, private dialogs: DialogService) {}

  get apiUrl(): string {
    return `/domain/cmdbDatabases/${this.databaseId}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.selectedConnectionId = undefined;
    this.selectedConnection = undefined;
    if (!changes.databaseId) {
      return;
    }
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.connections = [];
    this.selectedConnectionId = undefined;
    this.selectedConnection = undefined;
    this.saving = false;
    if (this.databaseId) {
      this.reload();
    }
  }

  reload(): void {
    this.listSubscription?.unsubscribe();
    this.loading = true;
    this.loadFailed = false;
    this.listSubscription = this.http.get<DatabaseSqlConnection[]>(`${this.env.e.url}${this.apiUrl}/sqlServers`)
      .subscribe({
        next: connections => {
          this.connections = connections;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.loadFailed = true;
        }
      });
    this.subscriptions.add(this.listSubscription);
  }

  link(): void {
    if (!this.databaseId || !this.sqlServerTypeId || !this.selectedConnectionId
        || this.selectedConnection?.sqlServerTypeId !== this.sqlServerTypeId
        || this.saving || this.loading || this.loadFailed) {
      return;
    }
    const name = this.selectedConnection?.name || 'connection';
    this.saving = true;
    this.subscriptions.add(this.http.put(`${this.env.e.url}${this.apiUrl}/sqlServers/${this.selectedConnectionId}`, {})
      .subscribe({
        next: () => {
          this.selectedConnectionId = undefined;
          this.selectedConnection = undefined;
          this.saving = false;
          this.alerts.info(`Linked ${name}`);
          this.reload();
        },
        error: () => this.saving = false
      }));
  }

  unlink(connection: DatabaseSqlConnection): void {
    if (this.saving || this.loading) {
      return;
    }
    this.subscriptions.add(this.dialogs.confirm('Remove connection',
      `Remove ${connection.name} from this database? The SQL connection will be kept.`, 'Remove')
      .subscribe(confirmed => {
        if (!confirmed || this.saving) {
          return;
        }
        this.saving = true;
        this.subscriptions.add(this.http.delete(`${this.env.e.url}${this.apiUrl}/sqlServers/${connection.id}`)
          .subscribe({
            next: () => {
              this.saving = false;
              this.alerts.info(`Removed ${connection.name}`);
              this.reload();
            },
            error: () => this.saving = false
          }));
      }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
